import { useState, useCallback } from 'react';

/**
 * Hook to manage all modal open/close states in the game.
 * Centralizes modal management for cleaner component code.
 */
export function useModals() {
  const [showSettings, setShowSettings] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showDailyChallenge, setShowDailyChallenge] = useState(false);
  const [showDailyPrompt, setShowDailyPrompt] = useState(false);
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showCreateChallenge, setShowCreateChallenge] = useState(false);
  const [showAvailableChallenges, setShowAvailableChallenges] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showSecretMenu, setShowSecretMenu] = useState(false);

  // Convenience methods
  const openSettings = useCallback(() => setShowSettings(true), []);
  const closeSettings = useCallback(() => setShowSettings(false), []);
  
  const openLeaderboard = useCallback(() => setShowLeaderboard(true), []);
  const closeLeaderboard = useCallback(() => setShowLeaderboard(false), []);
  
  const openDailyChallenge = useCallback(() => setShowDailyChallenge(true), []);
  const closeDailyChallenge = useCallback(() => setShowDailyChallenge(false), []);
  
  const openDailyPrompt = useCallback(() => setShowDailyPrompt(true), []);
  const closeDailyPrompt = useCallback(() => setShowDailyPrompt(false), []);
  
  const openFullscreenPrompt = useCallback(() => setShowFullscreenPrompt(true), []);
  const closeFullscreenPrompt = useCallback(() => setShowFullscreenPrompt(false), []);
  
  const openHelp = useCallback(() => setShowHelp(true), []);
  const closeHelp = useCallback(() => setShowHelp(false), []);
  
  const openCreateChallenge = useCallback(() => setShowCreateChallenge(true), []);
  const closeCreateChallenge = useCallback(() => setShowCreateChallenge(false), []);
  
  const openAvailableChallenges = useCallback(() => setShowAvailableChallenges(true), []);
  const closeAvailableChallenges = useCallback(() => setShowAvailableChallenges(false), []);
  
  const openHistory = useCallback(() => setShowHistory(true), []);
  const closeHistory = useCallback(() => setShowHistory(false), []);
  
  const openSecretMenu = useCallback(() => setShowSecretMenu(true), []);
  const closeSecretMenu = useCallback(() => setShowSecretMenu(false), []);

  // Check if any modal is open (used to hide floating elements)
  const isAnyModalOpen = 
    showSettings || 
    showLeaderboard || 
    showDailyChallenge || 
    showDailyPrompt || 
    showHelp || 
    showCreateChallenge || 
    showAvailableChallenges || 
    showHistory;

  return {
    // State
    showSettings,
    showLeaderboard,
    showDailyChallenge,
    showDailyPrompt,
    showFullscreenPrompt,
    showHelp,
    showCreateChallenge,
    showAvailableChallenges,
    showHistory,
    showSecretMenu,
    isAnyModalOpen,
    
    // Setters (for direct control)
    setShowSettings,
    setShowLeaderboard,
    setShowDailyChallenge,
    setShowDailyPrompt,
    setShowFullscreenPrompt,
    setShowHelp,
    setShowCreateChallenge,
    setShowAvailableChallenges,
    setShowHistory,
    setShowSecretMenu,
    
    // Convenience methods
    openSettings,
    closeSettings,
    openLeaderboard,
    closeLeaderboard,
    openDailyChallenge,
    closeDailyChallenge,
    openDailyPrompt,
    closeDailyPrompt,
    openFullscreenPrompt,
    closeFullscreenPrompt,
    openHelp,
    closeHelp,
    openCreateChallenge,
    closeCreateChallenge,
    openAvailableChallenges,
    closeAvailableChallenges,
    openHistory,
    closeHistory,
    openSecretMenu,
    closeSecretMenu
  };
}
